/** author：胡枭 */

import axios from 'axios'
import { MessageBox, Loading } from 'element-ui'
import store from '@/store'
import router from '@/router'
import requestErr from './request-err.js'
import Cookies from 'js-cookie'
import { getQueryFromURL } from '@/utils/utils'
import { workbenchServiceDict } from '@/dictionary/system'

// 模块全局局变量，标记是否正在刷新token
let isRefreshing = false
// 模块全局变量，重试队列，每一项将是一个待执行的函数形式
let requestArr = []
// 模块全局变量，标识用户信息已经失效，但是用户没有点击弹窗时，不再重复验证登录状态（项目里面有轮询）
let isLogout = false

const service = axios.create({
  // baseURL: process.env.VUE_APP_DOMAIN,
  withCredentials: true
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    const queryObject = getQueryFromURL(window.location.href)
    // 如果在cookie中获得XSRF-TOKEN，则写入到header中给后台校验
    const xsrf = Cookies.get('XSRF-TOKEN')
    if (xsrf) {
      config.headers['X-XSRF-TOKEN'] = xsrf
    }
    if ('formKey' in queryObject && config.method === 'post') {
      config.headers['X-FORM-KEY'] = queryObject.formKey
      config.headers['X-FORM-ROUTE'] = window.location.pathname
    }
    const ac = Cookies.get('X-JCZH-AC')
    if (ac && ac !== 'undefined' && ac !== 'null') {
      config.headers['X-JCZH-AC'] = ac
    }

    // 因赣江控股有项目辅助功能，而云采购没有，制定的暂时方式。由赣江控股跳转过的携带参数isIncludeCompany，managerEmpId标识，放开权限，查询公司项目
    const managerEmpId = window.sessionStorage.getItem('managerEmpId')
    if (managerEmpId) {
      config.headers['managerEmpId'] = managerEmpId
      config.headers['isIncludeCompany'] = managerEmpId ? 1 : 0
    }

    // 在可以获得到当前工作台的情况下，则写入到header中告诉后台用户当前所处的平台
    // 若当前平台没有身份，不传
    if (store.state.router.currentWorkbench) {
      config.headers['identity'] =
        workbenchServiceDict.translate(store.state.router.currentWorkbench)
          ?.platformAlias || ''
    }

    // header中设置接受语言的优先级
    config.headers['Accept-Language'] =
      'zh-CN,zh;q=0.9,en-US;q=0.7,en;q=0.8,zh-TW;q=0.6'

    let url = config.url
    // get参数编码
    if (config.method === 'get' && config.params) {
      url += '?'
      const keys = Object.keys(config.params)
      // eslint-disable-next-line no-unused-vars
      for (const key of keys) {
        const tempData =
          config.params[key] === '' ||
          config.params[key] === undefined ||
          config.params[key] == null
            ? ''
            : config.params[key]
        url += `${key}=${encodeURIComponent(tempData)}&`
      }
      url = url.substring(0, url.length - 1)
      config.params = {}
    }
    config.url = url

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  // http请求成功的响应拦截
  response => {
    const res = response.data

    // 如果是mock请求，则进行数据组装返回
    if (
      response.config.url.indexOf('mock') > -1 ||
      response.config.url.indexOf('m1') > -1
    ) {
      return { data: res }
    }

    // 如果是响应类型是blob，则直接返回
    if (response.config.responseType === 'blob') {
      return res
    }

    if (res.success) {
      return res
    } else if (res.code === '10008') {
      router.replace({ name: 'NotRight' })
    }

    response.message = res.message || requestErr[res.code] || '数据请求错误'
    return Promise.reject(response)
  },

  // http请求失败的响应拦截
  error => {
    const { code, message, response = {} } = error
    const config = response.config
    const status = response.status

    if (status == 401 && config.url.indexOf('/passport/status') === -1) {
      if (isRefreshing) {
        return new Promise(resolve => {
          requestArr.push(() => {
            resolve(service(config))
          })
        })
      }

      if (isLogout) return Promise.reject()
      isRefreshing = true
      return store
        .dispatch('user/checkLoginStatus')
        .then(res => {
          if (res.purchase) {
            isRefreshing = false
            requestArr.forEach(cb => cb())
            requestArr = []
            return service(config)
          } else {
            window.location.href = `${process.env.VUE_APP_PASSPORT_URL}/select-company`
          }
        })
        .catch(() => {
          MessageBox.alert('用户信息已过期，请重新登录', '系统提示', {
            confirmButtonText: '确定',
            showClose: false,
            callback: action => {
              // 防止当前页面有beforeunload的阻拦
              window.onbeforeunload = null
              // 此处需要修改跳转地址
              window.location.href = `${
                store.getters['company/loginAddress']
              }?returnUrl=${encodeURIComponent(window.location.href)}`
            }
          })

          isRefreshing = false
          isLogout = true
          return Promise.reject()
        })
    }

    if (response.data && response.data instanceof Object) {
      response.message =
        response.data.message || requestErr[status] || '数据请求错误'
    } else {
      response.message = requestErr[status] || '数据请求错误'
    }

    if (code === 'ECONNABORTED' || message === 'Network Error') {
      response.message = '网络错误'
    }

    return Promise.reject(response)
  }
)

/**
 * 将向后台请求的数据，进行操作；
 * 过滤空字符串的数据；
 * 如果isFormData为true，则将数据转换成FormData类型
 * @param {Object} data 需要进行操作的数据
 * @param {Boolean} isFormData 是否使用form表单的形式提交数据
 * @param {Boolean} isFilter 是否过滤空字符串地段
 */
function changeDataToFormData(data, isFormData, isFilter) {
  const formData = new FormData()
  const object = Object.create(null)

  // 如果传入的data不为对象，则直接返回
  if (Object.prototype.toString.call(data) !== '[object Object]') {
    return data
  }

  if (isFormData) {
    Object.keys(data).forEach(key => {
      if (data[key] !== '' || !isFilter) {
        formData.append(key, data[key])
      }
    })
    return formData
  } else {
    Object.keys(data).forEach(key => {
      if (data[key] !== '' || !isFilter) {
        object[key] = data[key]
      }
    })
    return object
  }
}

// 模块全局变量，loading计数器，实现多个接口请求，最后一个接口返回后才loading消失
let loadingNum = 0
let loadingInstance = null

/**
 * 对定义的axios进行再一次的封装，实现表单数据形式请求，loading和请求错误提示
 *
 * @param {Object} ajax  接口请求的配置参数
 * @param {Boolean} needLoading  是否需要全局的loading
 * @param {Boolean?} isFormData   是否使用表单数据形式请求接口
 * @returns {Promise<void>}   返回promise的axios实例
 */
export default function(ajax, needLoading = true, isFormData = false) {
  // 对数据进行 空字符串的过滤 和表单形式数据的转换，
  // 这里只能对get请求的数据，过滤空串
  ajax.data && (ajax.data = changeDataToFormData(ajax.data, isFormData, false))
  ajax.params &&
    (ajax.params = changeDataToFormData(ajax.params, isFormData, true))

  // 需要全局loading时的计数
  if (needLoading) {
    loadingNum++

    loadingInstance = Loading.service({
      fullscreen: true,
      body: true,
      customClass: 'z-index-loading',
      text: '正在加载，请稍后'
    })
  }

  return service(ajax).finally(() => {
    // loading消失的处理
    if (needLoading) {
      loadingNum--
      setTimeout(() => {
        if (loadingNum <= 0 && loadingInstance) loadingInstance.close()
      }, 0)
    }
  })
}
